import Sentry from '@sentry/react-native';
import { useEffect, useRef, useState } from 'react';

import { AuthFlowError, authenticateUser } from '../utils/authenticateUser';

/**
 * Hook for authenticated actions that need to wait for data after authentication.
 *
 * Provides automatic retry when data loads, error handling, and loading state.
 * Action must call `complete()` when done. Early returns keep retry loop active.
 *
 * IMPORTANT: Do NOT wrap action in useCallback - retry relies on capturing fresh state.
 *
 * See guides/AUTHENTICATION_FLOW.md Pattern 2 for detailed usage.
 */
export function useExecuteWithAuth<TArgs extends unknown[] = []>(
  actionFn: (...args: [...TArgs, () => void]) => Promise<void>,
  options: {
    onError?: (error: unknown) => void;
  } = {},
) {
  const [executionState, setExecutionState] = useState<{
    isExecuting: boolean;
    args: TArgs | null;
  }>({ isExecuting: false, args: null });
  const executionInProgressRef = useRef(false);
  const isMountedRef = useRef(true);

  // Track mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const complete = () => {
    if (isMountedRef.current) {
      setExecutionState({ isExecuting: false, args: null });
    }
  };

  // Effect to execute action once state is set
  // Re-runs when actionFn changes (component re-renders with new data)
  useEffect(() => {
    if (!executionState.isExecuting || executionInProgressRef.current) {
      return;
    }

    const executeAction = async () => {
      // Mark execution as in progress to prevent duplicate execution
      executionInProgressRef.current = true;

      try {
        // Pass stored args plus complete function to action
        const args = (executionState.args ?? []) as TArgs;
        await actionFn(...args, complete);
        // Action is responsible for calling complete() when done
      } catch (error) {
        console.log('Error executing action:', error);
        Sentry?.captureException(error);
        // Call consumer's error handler if provided
        options.onError?.(error);
        // Always complete to stop retry loop
        complete();
        // Don't re-throw - error is handled
      } finally {
        // Always reset ref to allow next retry attempt
        executionInProgressRef.current = false;
      }
    };

    void executeAction();
  }, [executionState, actionFn]);

  /**
   * Execute the wrapped action with authentication check
   * If not authenticated, shows auth modal and executes after successful login
   * If auth flow is interrupted (user dismisses or duplicate request), silently stops execution
   */
  const execute = async (...args: TArgs): Promise<void> => {
    try {
      await authenticateUser();
      // Auth successful - trigger execution
      executionInProgressRef.current = false; // Reset flag for new execution attempt
      if (isMountedRef.current) {
        setExecutionState({ isExecuting: true, args });
      }
    } catch (error) {
      // Auth flow interrupted - stop execution
      if (error instanceof AuthFlowError) {
        return; // Silently ignore - user cancelled or duplicate request
      }
      // Re-throw unexpected errors
      throw error;
    }
  };

  return {
    /** Execute the action with authentication check */
    execute,
    /** Whether the action is currently executing */
    isExecuting: executionState.isExecuting,
  };
}
